import {
  Church as ChurchIcon,
  Close as CloseIcon,
  Facebook as FacebookIcon,
  Groups as GroupsIcon,
  Language as LanguageIcon,
  Person as PersonIcon
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  MenuItem,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React from "react";
import { toast } from "react-toastify";
import CustomSelect from "../../../../components/ui/Select";
import CustomTextField from "../../../../components/ui/Textfield";
import { tokens } from "../../../../theme";
import patterbackground from "./pattern.jpeg";

interface ChurchStreamDetailsModalProps {
  open: boolean;
  onClose: () => void;
  leadPastor: string;
  congregationSize: string;
  websiteLink: string;
  facebookLink: string;
  onLeadPastorChange: (value: string) => void;
  onCongregationSizeChange: (value: string) => void;
  onWebsiteLinkChange: (value: string) => void;
  onFacebookLinkChange: (value: string) => void;
  onChurchPhotoUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  churchPhotoPreview: string | null;
  onSubmit: () => void;
  isSubmitting: boolean;
}

const CONGREGATION_SIZES = [
  { value: "0-50", label: "0-50 members" },
  { value: "51-100", label: "51-100 members" },
  { value: "101-250", label: "101-250 members" },
  { value: "251-500", label: "251-500 members" },
  { value: "501-1000", label: "501-1,000 members" },
  { value: "1001+", label: "1,000+ members" },
];

const ChurchStreamDetailsModal: React.FC<ChurchStreamDetailsModalProps> = ({
  open,
  onClose,
  leadPastor,
  congregationSize,
  websiteLink,
  facebookLink,
  onLeadPastorChange,
  onCongregationSizeChange,
  onWebsiteLinkChange,
  onFacebookLinkChange,
  onChurchPhotoUpload,
  churchPhotoPreview,
  onSubmit,
  isSubmitting,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const handleSubmit = () => {
    if (!leadPastor || !congregationSize) {
      toast.error("Please fill in all required fields");
      return;
    }
    onSubmit();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: "90vh" } }}
    >
      <Box
        sx={{
          position: "relative",
          p: 3,
          bgcolor: "#614338",
          color: "white",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `url(${patterbackground})`,
            backgroundRepeat: "repeat",
            backgroundSize: "contain",
            opacity: 0.1,
            zIndex: 0,
          }}
        />
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Typography variant="h4" fontWeight="bold">
            Church Details
          </Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{
            opacity: 0.9,
            mt: 1,
            color: colors.primary[500],
            position: "relative",
            zIndex: 1,
          }}
        >
          Help us personalize your free stream service
        </Typography>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {/* Lead Pastor */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Lead Pastor{" "}
            <span style={{ color: theme.palette.error.main }}>*</span>
          </Typography>
          <CustomTextField
            placeholder="e.g., Rev. John Smith"
            value={leadPastor}
            onChange={(e) => onLeadPastorChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Congregation Size */}
        <CustomSelect
          label="Select congregation size"
          value={congregationSize}
          onChange={(e) => onCongregationSizeChange(e.target.value as string)}
          startAdornment={
            <InputAdornment position="start">
              <GroupsIcon sx={{ color: "text.secondary" }} />
            </InputAdornment>
          }
        >
          {CONGREGATION_SIZES.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </CustomSelect>

        {/* Website Link */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Website Link{" "}
            <span style={{ color: "text.secondary" }}>(Optional)</span>
          </Typography>
          <CustomTextField
            placeholder="https://www.yourchurch.com"
            value={websiteLink}
            onChange={(e) => onWebsiteLinkChange(e.target.value)}
            type="url"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LanguageIcon sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Facebook Page Link */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Facebook Page Link{" "}
            <span style={{ color: "text.secondary" }}>(Optional)</span>
          </Typography>
          <CustomTextField
            placeholder="https://www.facebook.com/yourchurch"
            value={facebookLink}
            onChange={(e) => onFacebookLinkChange(e.target.value)}
            type="url"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FacebookIcon sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Church Image/Logo Upload */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Church Image/Logo{" "}
            <span style={{ color: "text.secondary" }}>(Optional)</span>
          </Typography>
          <Button
            component="label"
            fullWidth
            variant="outlined"
            sx={{
              py: churchPhotoPreview ? 1.5 : 3,
              borderRadius: 2,
              borderStyle: "dashed",
              borderWidth: 2,
              borderColor: "divider",
              color: "text.secondary",
              textTransform: "none",
              display: "flex",
              flexDirection: "column",
              gap: 1,
              "&:hover": {
                borderColor: colors.primary[400],
                bgcolor: `${colors.primary[800]} !important`,
              },
            }}
          >
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={onChurchPhotoUpload}
            />

            {!churchPhotoPreview && (
              <>
                <ChurchIcon sx={{ fontSize: 32, color: "text.secondary" }} />
                <Typography variant="body2" fontWeight="bold">
                  Upload church image or logo
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  PNG, JPG up to 10MB
                </Typography>
              </>
            )}

            {churchPhotoPreview && (
              <>
                <Box
                  component="img"
                  src={churchPhotoPreview}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: 150,
                    borderRadius: 1,
                  }}
                />
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Click to change image
                </Typography>
              </>
            )}
          </Button>
        </Box>

        {/* Divider */}
        {/* <Box
          sx={{
            my: 3,
            pt: 3,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Deceased Information
          </Typography>
        </Box> */}

        {/* Deceased Name */}
        {/* <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Name of the Deceased{" "}
            <span style={{ color: theme.palette.error.main }}>*</span>
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter full name"
            value={deceasedName}
            onChange={(e) => onDeceasedNameChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FavoriteIcon sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        </Box> */}

        {/* Deceased Photo Upload */}
        {/* <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Photo of the Deceased{" "}
            <span style={{ color: "text.secondary" }}>(Optional)</span>
          </Typography>
          <Button
            component="label"
            fullWidth
            variant="outlined"
            sx={{
              py: deceasedPhotoPreview ? 1.5 : 3,
              borderRadius: 2,
              borderStyle: "dashed",
              borderWidth: 2,
              borderColor: "divider",
              color: "text.secondary",
              textTransform: "none",
              display: "flex",
              flexDirection: "column",
              gap: 1,
              "&:hover": {
                borderColor: colors.primary[400],
                bgcolor: `${colors.primary[800]} !important`,
              },
            }}
          >
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={onDeceasedPhotoUpload}
            />

            {!deceasedPhotoPreview && (
              <>
                <UploadIcon sx={{ fontSize: 32, color: "text.secondary" }} />
                <Typography variant="body2" fontWeight="bold">
                  Upload photo
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  PNG, JPG up to 10MB
                </Typography>
              </>
            )}

            {deceasedPhotoPreview && (
              <>
                <Box
                  component="img"
                  src={deceasedPhotoPreview}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: 150,
                    borderRadius: 1,
                  }}
                />
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Click to change photo
                </Typography>
              </>
            )}
          </Button>
        </Box> */}

        {/* Submit Button */}
        <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            disabled={isSubmitting}
            sx={{
              py: 1.5,
              borderRadius: 2,
              color: "text.primary",
              borderColor: "divider",
              boxShadow: 0,
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            sx={{
              py: 1.5,
              borderRadius: 2,
              background: colors.primary[400],
              color: "white",
              "&:hover": {
                background: "#4d362d",
              },
            }}
          >
            {isSubmitting ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={20} sx={{ color: "white" }} />
                Submitting...
              </Box>
            ) : (
              "Submit Application"
            )}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ChurchStreamDetailsModal;
